import React, { useState, useEffect } from "react";
import Modal from "../components/Modal";
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  getCollections,
  getBrands,
  deleteField,
} from "../../firebase/firestoreService";

const SIZES_ALL = ["S", "M", "L", "XL", "2XL", "3XL"];
const PRESET_SECTIONS = ["Apparel", "Lights & Projectors", "Accessories", "Content Utilities", "Home Decor", "Electronics"];

const PRODUCT_TYPES = [
  { value: "apparel",     label: "Apparel" },
  { value: "lights",      label: "Lights & Projectors" },
  { value: "accessories", label: "Accessories" },
  { value: "content",     label: "Content" },
];

const TYPE_LABEL = Object.fromEntries(PRODUCT_TYPES.map((t) => [t.value, t.label]));

const emptyForm = () => ({
  name:        "",
  type:        "apparel",
  brand:       "",
  collection:  "",
  section:     "",
  price:       "",
  description: "",
  images:      [""],                                        // for non-apparel
  sizes:       ["S", "M", "L", "XL", "2XL", "3XL"],       // apparel only
  colors:      [{ name: "", hex: "#7C5C4A", image: "" }],  // apparel only
});

export default function ProductsSection() {
  const [products,    setProducts]    = useState([]);
  const [collections, setCollections] = useState([]);
  const [brands,      setBrands]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [modalOpen,   setModalOpen]   = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [form,        setForm]        = useState(emptyForm());
  const [saving,      setSaving]      = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [p, c, b] = await Promise.all([getProducts(), getCollections(), getBrands()]);
    setProducts(p);
    setCollections(c);
    setBrands(b);
    setLoading(false);
  };

  const openAdd  = () => { setEditing(null); setForm(emptyForm()); setModalOpen(true); };
  const openEdit = (product) => {
    setEditing(product);
    const base = { ...emptyForm(), ...product };
    // Normalise images for non-apparel: support old products that only have `image`
    if (product.type !== "apparel") {
      base.images = product.images?.length ? product.images : (product.image ? [product.image] : [""]);
    }
    setForm(base);
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setEditing(null); };

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const toggleSize = (sz) =>
    setForm((f) => ({
      ...f,
      sizes: f.sizes.includes(sz) ? f.sizes.filter((s) => s !== sz) : [...f.sizes, sz],
    }));

  const setColor = (i, key, val) =>
    setForm((f) => {
      const colors = [...f.colors];
      colors[i] = { ...colors[i], [key]: val };
      return { ...f, colors };
    });

  const addColorRow    = () => setForm((f) => ({ ...f, colors: [...f.colors, { name: "", hex: "#C8B49A", image: "" }] }));
  const removeColorRow = (i) => setForm((f) => ({ ...f, colors: f.colors.filter((_, idx) => idx !== i) }));

  const isApparel = form.type === "apparel";

  const handleSave = async () => {
    if (!form.name.trim())        { alert("Enter a product name."); return; }
    if (isApparel && !form.brand) { alert("Select a brand.");       return; }
    if (!form.price)              { alert("Enter a price.");        return; }

    let cleanImages;
    if (!isApparel) {
      cleanImages = form.images.filter((u) => u.trim());
      if (!cleanImages.length) { alert("Add at least one image URL."); return; }
    }

    setSaving(true);

    // Firestore payload — may contain FieldValue sentinels for deletes
    const data = { ...form, price: Number(form.price) };
    if (!isApparel) {
      data.images = cleanImages;
      data.image  = cleanImages[0];
      if (editing) {
        data.colors = deleteField();
        data.sizes  = deleteField();
      } else {
        delete data.colors;
        delete data.sizes;
      }
    }

    // Clean copy for local state — no sentinels
    const local = { ...form, price: Number(form.price) };
    if (!isApparel) {
      local.images = cleanImages;
      local.image  = cleanImages[0];
      delete local.colors;
      delete local.sizes;
    }

    if (editing) {
      await updateProduct(editing.id, data);
      setProducts((prev) => prev.map((p) => p.id === editing.id ? { ...p, ...local } : p));
    } else {
      const newId = await createProduct(data);
      setProducts((prev) => [{ ...local, id: newId }, ...prev]);
    }

    setSaving(false);
    closeModal();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    await deleteProduct(id);
    setProducts((prev) => prev.filter((p) => p.id !== id));
  };

  const thumbSrc = (p) => p.colors?.[0]?.image || p.images?.[0] || p.image || "";

  return (
    <div>
      <div className="dash-main__header">
        <h2 className="dash-main__title">Products</h2>
        <button className="btn-primary" onClick={openAdd}>+ Add Product</button>
      </div>

      {loading ? (
        <p className="dash-empty">Loading products…</p>
      ) : products.length === 0 ? (
        <p className="dash-empty">No products yet. Add your first one.</p>
      ) : (
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Section</th>
                <th>Collection</th>
                <th>Type</th>
                <th>Price</th>
                <th>Views</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id}>
                  <td>
                    <img className="dash-table__img" src={thumbSrc(p)} alt={p.name} />
                  </td>
                  <td><strong>{p.name}</strong></td>
                  <td>{p.section   || <span style={{ color: "#ccc" }}>—</span>}</td>
                  <td>{p.collection || <span style={{ color: "#ccc" }}>—</span>}</td>
                  <td>{TYPE_LABEL[p.type] || p.type}</td>
                  <td>MK {Number(p.price).toLocaleString()}</td>
                  <td style={{ color: "#555", fontWeight: 600 }}>{p.views || 0}</td>
                  <td>
                    <button className="btn-edit"   onClick={() => openEdit(p)}>Edit</button>
                    <button className="btn-danger" onClick={() => handleDelete(p.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <Modal title={editing ? "Edit Product" : "Add Product"} onClose={closeModal}>

          {/* Name + Type */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input className="form-input" value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. LED Projector X200" />
            </div>
            <div className="form-group">
              <label className="form-label">Type *</label>
              <select className="form-select" value={form.type}
                onChange={(e) => setField("type", e.target.value)}>
                {PRODUCT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Brand */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Brand {isApparel ? "*" : <span style={{ textTransform: "none", letterSpacing: 0, fontSize: 10, color: "#ccc" }}>(optional)</span>}
              </label>
              <select className="form-select" value={form.brand}
                onChange={(e) => setField("brand", e.target.value)}>
                <option value="">Select brand</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">
                Collection <span style={{ textTransform: "none", letterSpacing: 0, fontSize: 10, color: "#ccc" }}>(optional)</span>
              </label>
              <select className="form-select" value={form.collection}
                onChange={(e) => setField("collection", e.target.value)}>
                <option value="">None</option>
                {collections.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Section */}
          <div className="form-group">
            <label className="form-label">
              Section <span style={{ textTransform: "none", letterSpacing: 0, fontSize: 10, color: "#ccc" }}>(for grouped listing — e.g. Lights & Projectors)</span>
            </label>
            <input className="form-input" list="section-presets"
              placeholder="e.g. Lights & Projectors"
              value={form.section || ""}
              onChange={(e) => setField("section", e.target.value)} />
            <datalist id="section-presets">
              {PRESET_SECTIONS.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>

          {/* Price */}
          <div className="form-group">
            <label className="form-label">Price (MK) *</label>
            <input className="form-input" type="number" value={form.price}
              onChange={(e) => setField("price", e.target.value)}
              placeholder="e.g. 25000" />
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">
              Description <span style={{ textTransform: "none", letterSpacing: 0, fontSize: 10, color: "#ccc" }}>(optional)</span>
            </label>
            <textarea
              className="form-textarea"
              rows={3}
              placeholder="Short product description…"
              value={form.description || ""}
              onChange={(e) => setField("description", e.target.value)}
            />
          </div>

          {/* ── APPAREL FIELDS ── */}
          {isApparel && (
            <>
              <div className="form-group">
                <label className="form-label">Sizes</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {SIZES_ALL.map((sz) => (
                    <button key={sz} type="button" onClick={() => toggleSize(sz)}
                      style={{
                        padding: "5px 12px", border: "1px solid", borderRadius: 5,
                        fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                        borderColor: form.sizes.includes(sz) ? "#1a1a1a" : "#ddd",
                        background:  form.sizes.includes(sz) ? "#1a1a1a" : "none",
                        color:       form.sizes.includes(sz) ? "#fff"    : "#555",
                      }}>
                      {sz}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Colours & Images</label>
                {form.colors.map((c, i) => (
                  <div key={i} className="color-entry">
                    <input type="color" className="color-entry__swatch"
                      value={c.hex} onChange={(e) => setColor(i, "hex", e.target.value)} />
                    <input className="form-input" placeholder="Colour name (e.g. Brown)"
                      value={c.name} onChange={(e) => setColor(i, "name", e.target.value)} />
                    <input className="form-input" placeholder="Image URL"
                      value={c.image} onChange={(e) => setColor(i, "image", e.target.value)} />
                    <button className="btn-remove-row" onClick={() => removeColorRow(i)}
                      disabled={form.colors.length === 1}>×</button>
                  </div>
                ))}
                <button className="btn-add-row" onClick={addColorRow}>+ Add colour</button>
              </div>
            </>
          )}

          {/* ── NON-APPAREL IMAGE FIELDS ── */}
          {!isApparel && (
            <div className="form-group">
              <label className="form-label">
                Images *
                <span style={{ textTransform: "none", letterSpacing: 0, fontSize: 10, color: "#ccc", marginLeft: 6 }}>
                  (first image is the main image)
                </span>
              </label>
              {form.images.map((url, i) => (
                <div key={i} className="color-entry" style={{ marginBottom: 8 }}>
                  <input
                    className="form-input"
                    placeholder={i === 0 ? "Main image URL *" : "Additional image URL (optional)"}
                    value={url}
                    onChange={(e) => {
                      const imgs = [...form.images];
                      imgs[i] = e.target.value;
                      setField("images", imgs);
                    }}
                  />
                  {url.trim() && (
                    <img
                      src={url}
                      alt="preview"
                      style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, background: "#eee", flexShrink: 0 }}
                    />
                  )}
                  <button
                    className="btn-remove-row"
                    type="button"
                    onClick={() => setField("images", form.images.filter((_, idx) => idx !== i))}
                    disabled={form.images.length === 1}
                  >×</button>
                </div>
              ))}
              <button className="btn-add-row" type="button" onClick={() => setField("images", [...form.images, ""])}>
                + Add image
              </button>
            </div>
          )}

          <div className="form-actions">
            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Product"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
